import { useQueryClient } from '@tanstack/react-query';
import { toastConfig } from 'lib/reactToastify/config';
import { lazy, Suspense, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { queryKeys } from 'api/config';
import { logError } from 'lib/sentry/logError';
import { Loader } from 'shared';
import { removeFromArrayById } from 'shared_DEPRECATED/utils';
import lazyRetry from 'shared_DEPRECATED/utils/lazyRetry';

import {
  GoalBreakdownType__DEPRECATED,
  useDeleteGoalFocusAreaMutation,
} from 'features/goal';

import { modal, SIZES } from 'shared/components/Modal';

const GoalDeleteFocusAreaDialogContent = lazy(() =>
  lazyRetry(
    () => import('features/goal/components/FocusArea/DeleteDialog'),
    'GoalFocusAreaDeleteDialog'
  )
);

type TUseDeleteGoalFocusArea = {
  focusAreaId: string;
};

export const useDeleteGoalFocusArea = ({
  focusAreaId,
}: TUseDeleteGoalFocusArea) => {
  const queryClient = useQueryClient();
  const { goalId } = useParams();

  const { mutateAsync } = useDeleteGoalFocusAreaMutation({
    focusAreaId,
    options: {
      onMutate: async () => {
        await queryClient.cancelQueries({
          queryKey: [queryKeys.goal, goalId],
        });

        const previousValue = queryClient.getQueryData([
          queryKeys.goal,
          goalId,
        ]);

        queryClient.setQueryData<GoalBreakdownType__DEPRECATED>(
          [queryKeys.goal, goalId],
          (oldVal) => {
            return {
              ...oldVal!,
              focusAreas: removeFromArrayById({
                array: oldVal!.focusAreas,
                id: focusAreaId,
                idProperty: 'focusAreaId',
              }),
            };
          }
        );

        return { previousValue };
      },
      onSettled: () => {
        queryClient.invalidateQueries([queryKeys.goalBreakdown, goalId]);
      },
      onError: (err: any) => {
        logError(err);
      },
    },
  });

  const onSubmit = useCallback(async () => {
    try {
      await mutateAsync();
    } catch (err) {
      toast.error(
        "There's an error with deleting the focus area. Please try again later.",
        toastConfig
      );
    }
  }, [mutateAsync]);

  const openDeleteFocusAreaModal = useCallback(() => {
    modal.open({
      content: (
        <Suspense fallback={<Loader />}>
          <GoalDeleteFocusAreaDialogContent onSubmit={onSubmit} />
        </Suspense>
      ),
      props: {
        size: SIZES.MD,
      },
    });
  }, [onSubmit]);

  return {
    onDeleteFocusAreaClick: openDeleteFocusAreaModal,
  };
};
